import axios from '@/lib/api/axios'

/**
 * Slack API Service Layer
 * Provides methods to interact with the Slack integration service
 */
export const slackApi = {
  // OAuth Flow
  getOAuthUrl: context =>
    axios(`/oauth/url`, { method: 'GET' }, context, true, 'slack'),

  getWorkspaceCompletionStatus: (teamId, context) =>
    axios(
      `/workspace/${teamId}/completion-status`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  // Organization Management
  getOrganizationConnection: (orgId, context) =>
    axios(
      `/organization/${orgId}/connection`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  deleteOrganizationConnection: (orgId, context) =>
    axios(
      `/organization/${orgId}/connection`,
      { method: 'DELETE' },
      context,
      true,
      'slack',
    ),

  // Channel Management
  getOrganizationChannels: (orgId, context) =>
    axios(
      `/organization/${orgId}/channels`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  getAvailableChannels: (orgId, context) =>
    axios(
      `/organization/${orgId}/available-channels`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  addBotToChannels: (orgId, channelIds, teamId, context) =>
    axios(
      `/organization/${orgId}/add-channels`,
      {
        method: 'POST',
        data: { channelIds, teamId },
      },
      context,
      true,
      'slack',
    ),

  // Workspace Management
  getWorkspaceChannels: (teamId, context) =>
    axios(
      `/workspace/${teamId}/channels`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  getWorkspaceAvailableChannels: (teamId, context) =>
    axios(
      `/workspace/${teamId}/available-channels`,
      { method: 'GET' },
      context,
      true,
      'slack',
    ),

  addBotToChannel: (teamId, channelId, context) =>
    axios(
      `/workspace/${teamId}/channel/${channelId}/join`,
      { method: 'POST' },
      context,
      true,
      'slack',
    ),

  removeBotFromChannel: (teamId, channelId, context) =>
    axios(
      `/workspace/${teamId}/channel/${channelId}/leave`,
      { method: 'POST' },
      context,
      true,
      'slack',
    ),

  // Messaging
  sendMessage: (messageData, context) =>
    axios(
      '/message',
      {
        method: 'POST',
        data: messageData,
      },
      context,
      true,
      'slack',
    ),

  // Health & Status
  getServiceStatus: context =>
    axios('/status', { method: 'GET' }, context, true, 'slack'),

  getConfigStatus: context =>
    axios('/config/status', { method: 'GET' }, context, true, 'slack'),
}

/**
 * Slack OAuth Component Helper
 * Provides OAuth flow management for React components
 */
export const slackOAuthHelper = {
  /**
   * Start OAuth flow
   * @param {object} context - Auth context
   * @returns {Promise<object>} OAuth URL and state
   */
  startOAuth: async context => {
    try {
      const response = await slackApi.getOAuthUrl(context)
      return response.data
    } catch (error) {
      console.error('Failed to start OAuth flow:', error)
      throw new Error('Failed to generate OAuth URL')
    }
  },

  /**
   * Poll workspace completion status
   * @param {string} teamId - The Slack team ID
   * @param {object} context - Auth context
   * @returns {Promise<object>} Workspace completion status
   */
  pollWorkspaceCompletionStatus: async (teamId, context) => {
    try {
      const response = await slackApi.getWorkspaceCompletionStatus(
        teamId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to poll workspace completion status:', error)
      throw new Error('Failed to check workspace completion status')
    }
  },

  /**
   * Poll workspace completion status with automatic completion detection
   * @param {string} teamId - The Slack team ID
   * @param {object} context - Auth context
   * @param {function} onComplete - Callback when OAuth completes
   * @param {function} onError - Callback when OAuth fails
   * @returns {function} Function to stop polling
   */
  pollWorkspaceCompletionWithCallback: (
    teamId,
    context,
    onComplete,
    onError,
  ) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await slackOAuthHelper.pollWorkspaceCompletionStatus(
          teamId,
          context,
        )

        if (status.completed) {
          clearInterval(pollInterval)
          onComplete?.({ team_id: teamId, team_name: status.team_name })
        }
      } catch (error) {
        clearInterval(pollInterval)
        onError?.(error.message)
      }
    }, 2000)

    // Return function to stop polling
    return () => clearInterval(pollInterval)
  },
}

/**
 * Slack Channel Management Helper
 * Provides channel management utilities
 */
export const slackChannelHelper = {
  /**
   * Add bot to multiple channels
   * @param {string} organizationId - The organization ID
   * @param {string[]} channelIds - Array of channel IDs
   * @param {string} teamId - The Slack team ID
   * @param {object} context - Auth context
   * @returns {Promise<object>} Result with success/failure counts
   */
  addBotToChannels: async (organizationId, channelIds, teamId, context) => {
    try {
      const response = await slackApi.addBotToChannels(
        organizationId,
        channelIds,
        teamId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to add bot to channels:', error)
      throw new Error('Failed to add bot to channels')
    }
  },

  /**
   * Remove bot from a channel
   * @param {string} teamId - The Slack team ID
   * @param {string} channelId - The channel ID
   * @param {object} context - Auth context
   * @returns {Promise<object>} Result
   */
  removeBotFromChannel: async (teamId, channelId, context) => {
    try {
      const response = await slackApi.removeBotFromChannel(
        teamId,
        channelId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to remove bot from channel:', error)
      throw new Error('Failed to remove bot from channel')
    }
  },

  /**
   * Get available channels for an organization
   * @param {string} organizationId - The organization ID
   * @param {object} context - Auth context
   * @returns {Promise<object[]>} Array of available channels
   */
  getAvailableChannels: async (organizationId, context) => {
    try {
      const response = await slackApi.getAvailableChannels(
        organizationId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to get available channels:', error)
      throw new Error('Failed to load available channels')
    }
  },

  /**
   * Get channels where bot is present
   * @param {string} organizationId - The organization ID
   * @param {object} context - Auth context
   * @returns {Promise<object[]>} Array of connected channels
   */
  getConnectedChannels: async (organizationId, context) => {
    try {
      const response = await slackApi.getOrganizationChannels(
        organizationId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to get connected channels:', error)
      throw new Error('Failed to load connected channels')
    }
  },
}

/**
 * Slack Connection Management Helper
 * Provides connection management utilities
 */
export const slackConnectionHelper = {
  /**
   * Get organization's Slack connection
   * @param {string} organizationId - The organization ID
   * @param {object} context - Auth context
   * @returns {Promise<object|null>} Connection data or null if not connected
   */
  getConnection: async (organizationId, context) => {
    try {
      const response = await slackApi.getOrganizationConnection(
        organizationId,
        context,
      )
      return response.data
    } catch (error) {
      if (error.response?.status === 404) {
        return null // No connection exists
      }
      console.error('Failed to get connection:', error)
      throw new Error('Failed to load connection')
    }
  },

  /**
   * Remove organization's Slack connection
   * @param {string} organizationId - The organization ID
   * @param {object} context - Auth context
   * @returns {Promise<object>} Result
   */
  removeConnection: async (organizationId, context) => {
    try {
      const response = await slackApi.deleteOrganizationConnection(
        organizationId,
        context,
      )
      return response.data
    } catch (error) {
      console.error('Failed to remove connection:', error)
      throw new Error('Failed to remove connection')
    }
  },
}

export default slackApi
